import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  setActiveFeature,
  setActiveProject,
  setHoveredFeature,
  setActivePage,
} from "../../actions/ui";
import {
  selectActiveFeature,
  selectHoveredFeature,
  selectSearchedLocation,
  selectFilteredProjects,
  selectIsFramed,
  selectIsMultiple,
  selectActivePage,
  selectGeoJSON,
  selectBasemap,
  selectActiveMultipleProject,
} from "../../reducers/";
import { getPin } from "../../modules/fund";
import { centerFromProject, projectShape } from "../../modules/project";
import pointInPolygon from "@turf/boolean-point-in-polygon";
import bboxPolygon from "@turf/bbox-polygon";
import bboxTurf from "@turf/bbox";
import turfPoint from "turf-point";
import styles from "./map.module.scss";
import fullscreenIcon from "./fullscreen.svg";
import ReactMapGl, {
  SVGOverlay,
  FlyToInterpolator,
  Marker,
  NavigationControl,
} from "react-map-gl";
import WebMercatorViewport from "viewport-mercator-project";
import streetsMapStyle from "./streets-map-style.json";
import satteliteMapStyle from "./sattelite-map-style.json";
import Tooltip from "./tooltip";
import "mapbox-gl/dist/mapbox-gl.css";
import queryString from "query-string";

const BBOX = [
  140.99927903600008, -37.50507657099996, 153.63883173500005,
  -28.157020554999974,
];

const ICON_HEIGHT = 32;
const ICON_WIDTH = 24;
const ICON_HEIGHT_MULTIPLE = 48;
const ICON_WIDTH_MULTIPLE = 32;
const CIRCLE_RADIUS = 4;
const FOOTER_HEIGHT = 45;

const query = queryString.parse(window.location.search);

const createIsProjectInsideFeature = (feature) => {
  const featureBboxPoly = feature && bboxPolygon(bboxTurf(feature));

  return (project) => {
    if (!feature) return false;

    const point = turfPoint(centerFromProject(project));
    if (!pointInPolygon(point, featureBboxPoly)) {
      return false;
    }
    return pointInPolygon(point, feature);
  };
};
const initialViewport = {
  zoom: 10,
  width: window.innerWidth,
  height: query.sheetName
    ? query.sheetName.includes("regional-recovery-map")
      ? window.innerHeight - FOOTER_HEIGHT
      : window.innerHeight
    : window.innerHeight,
};

const initalViewState = new WebMercatorViewport(initialViewport).fitBounds(
  [BBOX.slice(0, 2), BBOX.slice(2, 4)],
  {
    padding: { top: 10, bottom: 10, left: 10, right: 10 },
  }
);

const BLUE = "#0082be";

const Map = (props) => {
  const [hoverIcon, setHoverIcon] = useState();
  const [hoverCircle, setHoverCircle] = useState();
  const [viewState, setViewState] = useState(initalViewState);

  const {
    idSelector,
    activeFeature,
    hoveredFeature,
    searchedLocation,
    geojson,
    isFramed,
    isMultiple,
    basemap,
    projects,
    setActiveProject,
    activeMultipleProject,
    activePage,
    setActivePage,
  } = props;
  const getBaseMap = () => {
    return basemap === "ROADMAP" ? streetsMapStyle : satteliteMapStyle;
  };

  useEffect(() => {
    if (!activeFeature) {
      setViewState({
        ...initalViewState,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      });
      return;
    }
    const bbox = bboxTurf(activeFeature);
    const newViewState = new WebMercatorViewport(viewState).fitBounds(
      [bbox.slice(0, 2), bbox.slice(2, 4)],
      {
        padding: { top: 10, bottom: 10, left: 10, right: 10 },
      }
    );

    setViewState((viewState) => {
      return {
        ...viewState,
        ...newViewState,
        transitionInterpolator: new FlyToInterpolator(),
        transitionDuration: 200,
      };
    });
  }, [activeFeature]);
  const basemapStyles = getBaseMap();
  const setActiveFeatureIfNeeded = (feature) => {
    if (!feature) {
      if (activeFeature) {
        setActiveFeature(null);
      }
      return;
    }
    const fullFeature = geojson.features.find(
      (x) => idSelector(x) === idSelector(feature)
    );
    if (idSelector(feature) !== idSelector(activeFeature)) {
      props.setActiveFeature(fullFeature);
    }
  };

  const fullscreenUrl = query.sheetName
    ? `/index.html?sheetName=${query.sheetName}`
    : `/index.html`;
  const fullscreenButton = isFramed && (
    <a
      className={styles.fullscreenButton}
      target="_parent"
      href={fullscreenUrl}
    >
      <img src={fullscreenIcon} alt="" />
    </a>
  );

  return (
    <ReactMapGl
      onViewportChange={(viewState) => {
        setViewState(viewState);
      }}
      {...viewState}
      mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_API_TOKEN}
      interactiveLayerIds={["fills"]}
      onHover={(e) => {
        if (!e.features || e.features.length === 0) {
          // hide if needed then return
          if (hoveredFeature) {
            setHoveredFeature(null);
          }
          return;
        }
        setHoveredFeature(e.features[0]);
      }}
      onClick={(e) => {
        hoverIcon && setHoverIcon(null);
        hoverCircle && setHoverCircle(null);

        if (!e.features || e.features.length === 0) {
          // hide if needed then return
          if (activeFeature) {
            setActiveFeatureIfNeeded(null);
          }
          return;
        }

        setActiveFeatureIfNeeded(e.features[0]);
        const newPage = activePage === "list" ? "map" : "list";
        setActivePage(newPage);
      }}
      width={"100%"}
      height={
        query.sheetName
          ? query.sheetName.includes("regional-recovery-map")
            ? window.innerHeight - FOOTER_HEIGHT
            : window.innerHeight
          : window.innerHeight
      }
      mapStyle={{
        ...basemapStyles,
        sources: {
          ...basemapStyles.sources,
          fills: {
            type: "geojson",
            data: geojson,
          },
          points: {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: projects.map((d) => {
                return turfPoint(centerFromProject(d), d);
              }),
            },
          },
        },
        layers: [
          ...basemapStyles.layers,
          {
            id: "fills",
            type: "fill",
            source: "fills",
            paint: {
              "fill-opacity": 0.3,
              "fill-color": [
                "case",
                [
                  "==",
                  ["get", props.idCol],
                  idSelector(hoveredFeature) || null,
                ],
                BLUE,
                ["==", ["get", props.idCol], idSelector(activeFeature) || null],
                BLUE,
                "transparent",
              ],
            },
          },
          {
            id: "borders",
            type: "line",
            source: "fills",
            paint: {
              "line-color": BLUE,
            },
          },
        ],
      }}
    >
      <SVGOverlay
        redraw={({ project }) => {
          const isProjectInsideFeature =
            createIsProjectInsideFeature(activeFeature);

          return projects.map((d, i) => {
            if (d.Project === "Re-build Urunga Tidal Pool") {
              return null;
            }
            let [x, y] = project([
              parseFloat(d.Longitude),
              parseFloat(d.Latitude),
            ]);
            const region = activeFeature
              ? idSelector(activeFeature.properties)
              : "no region";
            let iconDimensions = {
              width: ICON_WIDTH,
              height: ICON_HEIGHT,
            };
            if (d["Multiple locations"] === "Y") {
              iconDimensions = {
                width: ICON_WIDTH_MULTIPLE,
                height: ICON_HEIGHT_MULTIPLE,
              };
            }

            const circleMarker = (
              <circle
                onClick={(e) => setActiveProject(d)}
                onMouseEnter={(e) => setHoverCircle(d)}
                onMouseLeave={(e) => setHoverCircle(null)}
                key={"circle" + region + d.Project + i}
                data-key={"circle" + region + d.Project + i}
                cx={x}
                cy={y}
                r={CIRCLE_RADIUS}
                fill={"red"}
              />
            );

            const imageMarker = (
              <image
                onClick={(e) => setActiveProject(d)}
                onMouseEnter={(e) => setHoverIcon(d)}
                onMouseLeave={(e) => setHoverIcon(null)}
                key={region + d.Project + i}
                xlinkHref={
                  d["Multiple locations"] === "Y"
                    ? "img-multiple" + getPin(d.fundName)
                    : getPin(d.fundName)
                }
                height={iconDimensions.height}
                x={x}
                y={y}
                width={iconDimensions.width}
                data-key={"circle" + region + d.Project + i}
                style={{
                  transform: `translate(-${iconDimensions.width / 2}px,-${
                    iconDimensions.height
                  }px)`,
                }}
              />
            );

            // if displaying multiple projects across LGAs, use image
            // else if the project is inside the selected region, use image
            // else use default circle

            if (isMultiple) {
              return d.ID === activeMultipleProject.ID
                ? imageMarker
                : circleMarker;
            } else {
              return isProjectInsideFeature(d) ? imageMarker : circleMarker;
            }
          });
        }}
      />
      {fullscreenButton}
      {searchedLocation && (
        <Marker latitude={searchedLocation[1]} longitude={searchedLocation[0]}>
          <img
            style={{
              transform: `translate(-50%, -50%)`,
            }}
            alt="Marker icon, a star"
            src={"/img/star.svg"}
          />
        </Marker>
      )}
      <div className={styles.navigationControlBtns}>
        <NavigationControl showCompass={false} />
      </div>
      <Tooltip
        item={hoverCircle || hoverIcon}
        offsetTop={Boolean(hoverIcon) ? -ICON_HEIGHT : -CIRCLE_RADIUS}
        nameSelector={(d) => d.Project}
      />
    </ReactMapGl>
  );
};

const featureShape = {
  type: PropTypes.string.isRequired,
  geometry: PropTypes.object.isRequired,
  properties: PropTypes.object.isRequired,
};
Map.propTypes = {
  idSelector: PropTypes.func.isRequired,
  projects: PropTypes.arrayOf(PropTypes.shape(projectShape)),
  activeFeature: PropTypes.shape(featureShape),
  setActiveFeature: PropTypes.func.isRequired,
  setHoveredFeature: PropTypes.func.isRequired,
  setActiveProject: PropTypes.func.isRequired,
  searchedLocation: PropTypes.arrayOf(PropTypes.number),
  isFramed: PropTypes.bool,
  isMultiple: PropTypes.bool,
  basemap: PropTypes.string.isRequired,
  idCol: PropTypes.string.isRequired,
  geojson: PropTypes.shape({
    type: PropTypes.string.isRequired,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        geometry: PropTypes.object.isRequired,
        properties: PropTypes.object.isRequired,
      }).isRequired
    ),
  }),
  activeMultipleProject: PropTypes.shape(projectShape),
  setActivePage: PropTypes.func.isRequired,
  activePage: PropTypes.string.isRequired,
  hoveredFeature: PropTypes.string,
};

const mapStateToProps = (state) => ({
  activeFeature: selectActiveFeature(state),
  searchedLocation: selectSearchedLocation(state),
  projects: selectFilteredProjects(state),
  isFramed: selectIsFramed(state),
  isMultiple: selectIsMultiple(state),
  geojson: selectGeoJSON(state),
  basemap: selectBasemap(state),
  activeMultipleProject: selectActiveMultipleProject(state),
  hoveredFeature: selectHoveredFeature(state),
  activePage: selectActivePage(state),
});
const mapDispatchToProps = {
  setActiveFeature,
  setActiveProject,
  setHoveredFeature,
  setActivePage,
};

export default connect(mapStateToProps, mapDispatchToProps)(Map);



// WEBPACK FOOTER //
// ./src/components/map/map.js